// local imports
import './StatisticsSummary.scss'
import loaderLogo from 'assets/icons/loading-statistics.png'
import { ReactComponent as Office } from 'assets/icons/card-building.svg'
import { ReactComponent as CampusServices } from 'assets/icons/campus-services.svg'
import People from 'assets/icons/people-icon.png'
import { roundPercentages } from 'utils/Miscellaneous'
import useChartSummary from './useChartSummary'
import Loader from 'components/ui/Loader'
import StatisticsChartCard from '../../chart-containers/summary-statistic-chartcard/StatisticsChartCard'
import FirmDropdownMenu from '../../chart-components/dropdown-menu/FirmDropdownMenu'
import LocationDropdownMenu from '../../chart-components/dropdown-menu/LocationDropdownMenu'
import SwitchButtonComponent from '../../chart-components/single-selection-button/SwitchButtonComponent'
import CampusSummaryChart from 'components/dashboard/chart-containers/summary-statistic-chartcard/CampusSummaryChart'
import { ICampusServiceExists } from 'service/CampusService'
import { showLoading } from '../../DashboardHomePage'
import EmployeeSummaryChart from 'components/dashboard/chart-containers/summary-statistic-chartcard/EmployeeSummaryChart'

interface CompProps {
  userInfo: any
  firmList: any
  campusVisible: ICampusServiceExists | undefined
}

export default function StatisticsSummary(props: CompProps) {
  const {
    t,
    handleFirmChange,
    handleLocationChange,
    campusSummaryObj,
    summary,
    showError,
    showLoader,
    pageLoading,
    options,
    selectedCompany,
    setSelectedCompany,
    locationList,
    setSelectedLocation,
    selectedLocation,
    value,
    setValue,
  } = useChartSummary(props.userInfo, props.firmList)

  const {
    campusSummaryTotal,
    healthCount,
    healthPercent,
    coiffeurCount,
    coiffeurPercent,
    gymCount,
    gymPercent,
    lactationCount,
    lactationPercent,
  } = campusSummaryObj

  const renderStatistics = () => {
    return (
      <div className="statistic-wrapper">
        {value === 'work' ? (
          <>
            <StatisticsChartCard
              icon={<Office fill="#1ED2FF" stroke="#1ED2FF" />}
              label={t('dashboard.num-of-firm-office')}
              number={summary?.totalOffice || 0}
              inOutData={{
                total: { in: summary?.inTotalOffice || 0, out: summary?.outTotalOffice || 0 },
                desked: { in: summary?.inTotalOfficeDesked || 0, out: summary?.outTotalOfficeDesked || 0 },
                undesked: { in: summary?.inTotalOfficeUndesked || 0, out: summary?.outTotalOfficeUndesked || 0 },
              }}
              color={{ firstColor: '#18A6C9', secondColor: '#1ED2FF' }}
              legends={{
                firstLegend: t('dashboard.floor-with-table'),
                secondLegend: t('dashboard.floor-without-table'),
              }}
              data={{
                firstValue: {
                  number: summary?.totalOfficeDesked || 0,
                  percent: summary?.totalOfficeDeskedPercentage || 0,
                },
                secondValue: {
                  number: summary?.totalOfficeUndesked || 0,
                  percent: summary?.totalOfficeUndeskedPercentage || 0,
                },
              }}
            />

            <StatisticsChartCard
              icon={<Office fill="#198754" stroke="#198754" />}
              label={t('dashboard.num-of-shared-office')}
              number={summary?.totalSharedOffice || 0}
              color={{ firstColor: '#198754', secondColor: '#20c997' }}
              inOutData={{
                total: { in: summary?.inTotalSharedOffice || 0, out: summary?.outTotalSharedOffice || 0 },
                desked: { in: summary?.inTotalSharedOfficeDesked || 0, out: summary?.outTotalSharedOfficeDesked || 0 },
                undesked: {
                  in: summary?.inTotalSharedOfficeUndesked || 0,
                  out: summary?.outTotalSharedOfficeUndesked || 0,
                },
              }}
              legends={{
                firstLegend: t('dashboard.floor-with-table'),
                secondLegend: t('dashboard.floor-without-table'),
              }}
              data={{
                firstValue: {
                  number: summary?.totalSharedOfficeDesked || 0,
                  percent: summary?.totalSharedOfficeDeskedPercentage || 0,
                },
                secondValue: {
                  number: summary?.totalSharedOfficeUndesked || 0,
                  percent: summary?.totalSharedOfficeUndeskedPercentage || 0,
                },
              }}
            />

            <EmployeeSummaryChart
              icon={<img src={People} width="25px" alt="people-stats" />}
              label={t('dashboard.system-defined-users')}
              number={summary?.totalEmployee || 0}
              color={{ firstColor: '#6372c3', secondColor: '#acb4df' }}
              legends={{ firstLegend: t('dashboard.active'), secondLegend: t('dashboard.passive') }}
              data={{
                firstValue: {
                  number: summary?.totalActiveEmployee || 0,
                  percent: summary?.totalActiveEmployeePercentage || 0,
                },
                secondValue: {
                  number: summary?.totalInactiveEmployee || 0,
                  percent: summary?.totalInactiveEmployeePercentage || 0,
                },
                thirdValue: {
                  number: summary?.totalInactiveEmployee || 0,
                  percent: summary?.totalInactiveEmployeePercentage || 0,
                },
              }}
            />
          </>
        ) : (
          <>
            <CampusSummaryChart
              icon={<CampusServices width={35} height={35} />}
              label={t('dashboard.num-of-campus-services')}
              number={campusSummaryTotal || 0}
              color={{ firstColor: '#5868B8', secondColor: '#7986C6', thirdColor: '#9096B8', fourthColor: '#BFC3DB' }}
              legends={{
                firstLegend: t('campus-services.health-service'),
                secondLegend: t('campus-services.coiffeur'),
                thirdLegend: t('campus-services.gym'),
                fourthLegend: t('campus-services.lactation-room'),
              }}
              data={{
                firstValue: { number: healthCount, percent: roundPercentages([healthPercent]) },
                secondValue: { number: coiffeurCount, percent: roundPercentages([coiffeurPercent]) },
                thirdValue: { number: gymCount, percent: roundPercentages([gymPercent]) },
                fourthValue: { number: lactationCount, percent: roundPercentages([lactationPercent]) },
              }}
            />
          </>
        )}
      </div>
    )
  }

  const renderErrorMessage = () => {
    return (
      <div className={'h-100 error-message-container'}>
        <h3>{t('overlaypanel-texts.error-text')}</h3>
      </div>
    )
  }

  return (
    <>
      {pageLoading && showLoading()}

      <div className="col-md-12 statistics d-flex flex-column">
        <div className="statistics-header">
          <div className="statistics-choice-menu">
            <div className="statistic-title">{t('dashboard.general-summary')}</div>
            <div className="p-dropdown">
              <FirmDropdownMenu
                selected={selectedCompany}
                setSelected={setSelectedCompany}
                firmList={props.firmList}
                getData={handleFirmChange}
                onFocus={() => {}}
              />
            </div>
            <div className="p-dropdown">
              <LocationDropdownMenu
                selectedCompany={selectedCompany}
                firmList={props.firmList}
                setSelectedLocation={setSelectedLocation}
                getData={handleLocationChange}
                selectedLocation={selectedLocation}
                locationList={locationList}
                onFocus={() => {}}
                userInfo={props.userInfo}
              />
            </div>
          </div>

          {!!props.campusVisible && props.campusVisible?.isAnyExist ? (
            <div id="statistics-type">
              <SwitchButtonComponent
                optionLabel="name"
                optionValue="value"
                selected={value}
                setSelected={setValue}
                options={options}
                unselectable={false}
              />
            </div>
          ) : null}
        </div>

        <>
          {!showLoader && !showError ? (
            renderStatistics()
          ) : showLoader ? (
            <div style={{ height: '174px' }}>
              <Loader src={loaderLogo} />
            </div>
          ) : showError ? (
            renderErrorMessage()
          ) : null}
        </>
      </div>
    </>
  )
}
